import { useEffect, useRef, useState } from 'react'
import { useEscape } from '../../hooks/useEscape'
import { useTexte } from '../../texte'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'

import type { Grossmedium, Media, Reaction } from '../../api/types'
import { ChevronLeftIcon, ChevronRightIcon, PinIcon, ReplyIcon, SichernIcon, SprechblaseIcon, XIcon } from '../../components/Icons'
import { sichereFoto } from '../../lib/sichern'
import { saveMedia, saveMediaWords } from './saveMedia'
import {
  abstand,
  begrenze,
  fuellFaktor,
  zoomeAuf,
  HAND_DECKEL,
  type Masse,
  type Punkt,
} from '../../lib/bildzoom'
import { Reactions } from './Reactions'
import { VideoPlayer } from './VideoPlayer'
import { Standbild } from '../../components/Standbild'
import { dauerText, standbildAdresse } from '../../lib/video'
import type { CollageStyle } from '../../lib/collage'
import { Collage } from './Collage'

/**
 * Die Fotos **und Videos** eines Beitrags.
 *
 * Ein einzelnes Bild steht für sich und behält sein Seitenverhältnis -- ein
 * Hochformat vom Telefon in ein Quadrat zu schneiden nimmt gerade das weg,
 * worum es ging. Ab zwei Bildern wird daraus ein Raster aus Quadraten, denn
 * nebeneinander gestellte Bilder unterschiedlicher Form ergeben eine unruhige
 * Treppe.
 *
 * Ab dem fünften Bild zeigt die letzte Kachel, wie viele noch folgen. Die
 * Großansicht hat sie ohnehin alle.
 *
 * **Und das Video gehört seit 1.56.0 dazu** (#86). Vorher stand es darüber
 * als eigener Player, an drei Stellen gleich abgeschrieben, mit der
 * Begründung: ein Player im Raster wäre auf ein Vorschaubild geschrumpft,
 * und die Großansicht sei fürs Blättern gebaut, nicht fürs Abspielen. Der
 * zweite Teil stimmt nicht mehr -- die Großansicht spielt jetzt ab.
 *
 * **Die Regel steht hier und nur hier**, damit Chat, Punkt und Spot nicht
 * wieder auseinanderlaufen: trägt ein Beitrag **genau ein** Medium und ist
 * das ein Video, steht es groß wie bisher. Sonst ist es eine Kachel neben
 * den Fotos, und der Tipp öffnet die Großansicht. Aus dem Betrieb, und es
 * ist die Abwägung zwischen sofort sehen und einheitlich bedienen: „wenn in
 * einer Nachricht mehrere Medien sind: Kachel. wenn nur ein Video: wie
 * bisher."
 */
export function Gallery({
  media,
  reactions,
  canReact,
  onReact,
  onReply,
  collage,
}: {
  media: Media[]
  /**
   * The style of a collage (1.168.0): the same tiles, the same large view
   * and the same reactions, only arranged by the collage instead of the
   * grid -- one gallery and not a second one beside it.
   */
  collage?: CollageStyle | null
  reactions: Reaction[]
  canReact: boolean
  /** `mediaId` is absent for the picture of a collage (1.169.0): it goes to the post. */
  onReact: (emoji: string, mediaId?: number) => void
  /**
   * Auf genau dieses Foto antworten.
   *
   * Der Knopf sitzt nur in der Großansicht und nicht auf der Kachel: die
   * öffnet die Großansicht, und ein zweites Ziel auf 56 Pixeln ist Gefummel.
   * Erst groß sieht man ohnehin, welches Bild man meint.
   */
  onReply?: (mediaId?: number) => void
}) {
  const t = useTexte()
  const [open, setOpen] = useState<number | null>(null)

  if (media.length === 0) return null

  // Ein Video allein bleibt der Player -- die eine Stelle für diese Regel.
  const einzelnesVideo = media.length === 1 && media[0]?.kind === 'video'
  if (einzelnesVideo) return <VideoPlayer media={media[0]!} />

  /*
   * **The picture of a collage is the post** (1.169.0): whoever reads along
   * gets one picture instead of the photos, and its `id` only holds the
   * first photo's place. A reaction in its large view and a reply to it go
   * to the post; its tile carries no badge, the bubble shows the post's.
   */
  const aufBeitrag = media.length === 1 && media[0]?.collage_picture === true
  const fuer = (id: number) =>
    aufBeitrag ? [] : reactions.filter((reaction) => reaction.media_id === id)
  const imGrossen = (id: number) =>
    aufBeitrag ? reactions.filter((reaction) => reaction.media_id === 0) : fuer(id)

  const shown = media.length > 4 ? media.slice(0, 4) : media
  const hidden = media.length - shown.length

  const kacheln = shown.map((item, index) => (
    <button
      key={item.id}
      type="button"
      className="gallery__tile"
      onClick={() => setOpen(index)}
      // A video says it is one, also to VoiceOver (1.170.0).
      aria-label={item.kind === 'video'
        ? t.chat.videoAbspielenNr(index + 1, media.length)
        : t.chat.fotoVergroessern(index + 1, media.length)}
    >
      {item.kind === 'video' ? (
        /* **Dieselbe Kachel wie auf der Medienseite** (#86): das
           Standbild, darüber die Marke mit der Dauer. `Standbild` holt
           es nach, wenn es beim ersten Zeichnen noch nicht fertig war. */
        <>
          <Standbild src={standbildAdresse(item) ?? ''} loading="lazy" />
          <span className="gallery__abspielen">{dauerText(item.duration_ms ?? 0)}</span>
        </>
      ) : (
        <Standbild
          /* **Die Kachel nimmt das Vorschaubild** (#57). Sie ist rund 120
             Punkte breit und lud bis hierher das volle Foto -- bei zwanzig
             Fotos zwanzig Megabyte, und genau die wirft iOS als Erstes aus
             dem Zwischenspeicher. Fehlt es (alles von vor #57), steht das
             Original darin wie bisher.

             **Das einzelne Foto nimmt die Datei** (1.78.0). Es steht bis
             55vh hoch, und die Vorschau hat 800 Bildpunkte -- gerechnet
             für eine Kachel von 320 Punkten; am iPhone (3×) wurde daraus
             rund das Doppelte hochgezogen, und es sah weich aus. Die Datei
             ist beim Hochladen schon verkleinert.

             **Und es ist `Standbild`, kein nacktes `<img>`** (1.78.0): das
             fragt nach, wenn das Netz wiederkommt -- sonst blieb nach dem
             Öffnen ohne Netz das Zeichen für ein kaputtes Bild stehen. */
          src={media.length === 1 ? item.url : (item.thumb_url ?? item.url)}
          loading="lazy"
          // Die Maße stehen in der Antwort. Der Platz ist damit reserviert,
          // bevor das Bild da ist, und der Verlauf springt beim Laden nicht.
          width={item.width}
          height={item.height}
        />
      )}
      {hidden > 0 && index === shown.length - 1 && (
        <span className="gallery__more">+{hidden}</span>
      )}

      {/* Was an diesem Foto hängt, steht auf dem Foto -- sonst wüsste
          bei vier Bildern niemand, welches gemeint ist. */}
      {fuer(item.id).length > 0 && (
        <span className="gallery__reactions">
          {fuer(item.id).map((reaction) => (
            <span key={reaction.emoji}>
              {reaction.emoji}
              {reaction.count > 1 && reaction.count}
            </span>
          ))}
        </span>
      )}
    </button>
  ))

  return (
    <>
      {collage ? (
        <Collage style={collage} tiles={kacheln} />
      ) : (
      <div
        className={media.length === 1 ? 'gallery gallery--single' : 'gallery'}
        // **`minmax(0, 1fr)` and not `1fr`** (1.88.1). From use, on the iPad:
        // a post of four pictures lost its third, and the chat showed two
        // tiles of the old size while the large view said "3 von 3". A bare
        // `1fr` is `minmax(auto, 1fr)`, and Safari takes the automatic
        // minimum of a square tile from its height -- the row height of the
        // two-column grid. Three columns of that width do not fit, and
        // `overflow: hidden` cut the third off. Chromium does not do this,
        // so the bench cannot show it; the zero takes away what it needs.
        style={media.length > 1 ? { gridTemplateColumns: `repeat(${media.length === 3 ? 3 : 2}, minmax(0, 1fr))` } : undefined}
      >
        {kacheln}
      </div>
      )}

      {open !== null && (
        <Lightbox
          media={media}
          start={open}
          reactionsFor={imGrossen}
          canReact={canReact}
          onReact={(emoji, mediaId) => onReact(emoji, aufBeitrag ? undefined : mediaId)}
          onReply={onReply && ((mediaId) => onReply(aufBeitrag ? undefined : mediaId))}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  )
}


/**
 * Großansicht.
 *
 * Schließt mit Escape, Klick auf den Hintergrund oder Wischen. Geblättert wird
 * mit den Pfeiltasten -- am Telefon mit dem Daumen.
 *
 * **Sie wird an den Seitenkörper gehängt, nicht dorthin, wo sie steht.** Auf
 * der Kartenseite lag sie sonst unter der Karte (Leaflet baut seine Ebenen bis
 * Rang 1000) und im Formular wurde sie vom scrollenden Blatt abgeschnitten --
 * am iPad und am iPhone genau so gemeldet. Ein Bild in Großansicht gehört
 * keinem Kasten, es gehört dem Bildschirm.
 *
 * **Und seit #60 gehört ihm auch die Fläche** — der 16-Punkte-Rahmen ist weg,
 * Kopf und Leiste liegen über dem Bild und treten bei einem Tipp beiseite.
 * Aus dem Betrieb: „Fotos im Querformat in der Galerieansicht sollen auch auf
 * den ganzen Bildschirm vergrößert werden." Gemessen war ein Querformat 4:3
 * am iPhone 358 × 268 Punkte groß — ein Viertel der Fläche, der Rest dunkel.
 * Am iPad fiel es nicht auf, weil dort das Fenster fast dasselbe Verhältnis
 * hat wie das Bild; es war also nie ein Fehler eines Geräts, sondern eine
 * Regel, die nur bei passendem Verhältnis aufgeht.
 *
 * **Ein Rahmen allein reicht dafür aber nicht.** Ein liegendes Foto auf einem
 * stehenden Schirm füllt ihn nie, solange es ganz zu sehen sein soll. Deshalb
 * der Zoom: Doppeltipp, zwei Finger, schieben. Nichts wird beschnitten — es
 * wird herangeholt, und der Ausschnitt gehört dem, der schaut.
 */
export function Lightbox({
  media,
  start,
  reactionsFor,
  canReact,
  onReact,
  onReply,
  wegZu,
  onClose,
}: {
  /**
   * **Der schmale Typ und nicht `Media`.** Hier kommt seit dem Zitat auch ein
   * Bild an, von dem es kein ganzes Medium gibt -- und ein `Media` mit
   * erfundenen Feldern wäre eine Lüge, die niemand später sucht.
   */
  media: Grossmedium[]
  start: number
  reactionsFor: (mediaId: number) => Reaction[]
  canReact: boolean
  onReact: (emoji: string, mediaId: number) => void
  onReply?: (mediaId: number) => void
  /**
   * Wohin dieses Medium gehört (#86, 1.56.0): sein Beitrag im Chat oder sein
   * POI auf der Karte.
   *
   * Die Medienseite hatte diesen Weg an ihrem eigenen Videoblatt; mit dem
   * Video in der Großansicht wäre er sonst verlorengegangen. Nach der
   * Kennung gefragt und nicht nach dem Medium, damit die Seite in ihrer
   * eigenen Liste nachsehen kann -- die Großansicht kennt weder Beitrag
   * noch POI.
   */
  wegZu?: (mediaId: number) => { ziel: string; wort: string; art: 'beitrag' | 'punkt' } | null
  onClose: () => void
}) {
  const t = useTexte()
  // The top layer while it stands: a sheet below it keeps its sheet open.
  useEscape(onClose)
  const [index, setIndex] = useState(start)
  /**
   * Wo der Finger aufsetzte — als Zettel, nicht als Zustand.
   *
   * **Hier stand `useState`, und das ist die falsche Schublade.** Der Wert
   * wird nirgends gezeichnet; gebraucht wird er nur im nächsten Ereignis. Als
   * Zustand hängt er daran, dass zwischen Aufsetzen und Loslassen wirklich
   * neu gezeichnet wurde — bei einem Finger stimmt das, bei einem schnellen
   * Wisch ist es Glück. `HighlightBlatt` macht es mit `griff` seit jeher so,
   * und genau dieser Unterschied fiel beim Nachmessen auf: dessen Blättern
   * löste aus, das der Großansicht nicht.
   *
   * Seit #60 trägt derselbe Zettel mehr: die Lage des Ausschnitts beim
   * Aufsetzen, damit das Schieben nicht auf ein Neuzeichnen wartet.
   */
  const griff = useRef<{ x: number; y: number; versatz: Punkt; zeit: number } | null>(null)

  /**
   * Was beim Sichern herausgekommen ist — als Satz, nicht als `title`.
   *
   * **Keine stille Absage:** wer den Knopf antippt und nichts sieht, hält die
   * Anwendung für kaputt. Auf dem Telefon spricht meist das Teilen-Blatt für
   * sich; dort bleibt es still. Gesagt wird, was der Mensch sonst nicht sieht:
   * dass es lief, dass es geladen wurde, oder dass es schiefging.
   */
  const [meldung, setMeldung] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  // --- Zoom und Ausschnitt (#60) --------------------------------------------

  /** 1 heißt eingepasst — das ganze Bild ist zu sehen. */
  const [zoom, setZoom] = useState(1)
  const [versatz, setVersatz] = useState<Punkt>({ x: 0, y: 0 })
  /**
   * Ob die Bewegung geglättet wird.
   *
   * **Beim Doppeltipp ja, beim Ziehen nein.** Eine Überblendung unter dem
   * Finger hinkt hinterher und fühlt sich an, als klemme etwas.
   */
  const [sanft, setSanft] = useState(true)
  /** Knöpfe und Leiste ausgeblendet — ein Tipp aufs Bild schaltet um. */
  const [nackt, setNackt] = useState(false)

  /**
   * Die wahre Größe des Bildes.
   *
   * Sie steht in der Antwort des Servers (`width`/`height`) und wird beim
   * Laden noch einmal am Element nachgesehen: ein Bild von vor der Zeit, als
   * die Maße mitkamen, hat dort Null stehen, und mit Null stimmt keine der
   * Rechnungen.
   */
  const [natur, setNatur] = useState<Masse | null>(null)

  const flaeche = useRef<HTMLDivElement>(null)
  /** Zwei Finger: der Abstand beim Aufsetzen und der Zoom dazu. */
  const hand = useRef<{ abstand: number; zoom: number } | null>(null)
  /** Der vorige Tipp — für die Unterscheidung von Einzel- und Doppeltipp. */
  const letzterTipp = useRef<{ zeit: number; x: number; y: number } | null>(null)
  /** Die Uhr, die auf den zweiten Tipp wartet. */
  const tippUhr = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Die Maus zieht am vergrößerten Bild — am Rechner gibt es keinen Finger. */
  const maus = useRef<{ x: number; y: number; versatz: Punkt } | null>(null)
  /** Wann zuletzt ein Finger oben war — gegen den nachgestellten Klick. */
  const zuletztFinger = useRef(0)
  /** Ob die Berührung auf einem Bedienelement begann und nicht auf dem Bild. */
  const imChrom = useRef(false)
  /**
   * Zeigt die Großansicht gerade ein Video? (#86, 1.56.0)
   *
   * Drei Gesten hängen daran und gelten dann nicht: Zoom mit dem Rad,
   * Doppeltipp und das Nachziehen mit der Maus. Ein Video hat keinen
   * Ausschnitt, in den man hineingeht; was es an Gesten braucht, macht sein
   * eigener Player, und der behält sie über die Zielprüfung in
   * `onTouchStart`.
   */
  const istVideo = media[index]?.kind === 'video'

  const bild: Masse = natur ?? {
    breite: media[index]?.width ?? 0,
    hoehe: media[index]?.height ?? 0,
  }

  function fenster(): Masse {
    const kasten = flaeche.current?.getBoundingClientRect()
    return { breite: kasten?.width ?? 0, hoehe: kasten?.height ?? 0 }
  }

  /** Ein Bildschirmpunkt, gezählt von der Mitte der Fläche aus. */
  function abMitte(clientX: number, clientY: number): Punkt {
    const kasten = flaeche.current?.getBoundingClientRect()
    if (kasten === undefined) return { x: 0, y: 0 }
    return {
      x: clientX - (kasten.left + kasten.width / 2),
      y: clientY - (kasten.top + kasten.height / 2),
    }
  }

  function setzeZoom(neu: number, aufPunkt: Punkt | null, weich: boolean) {
    const geklemmt = Math.min(Math.max(neu, 1), HAND_DECKEL)
    setSanft(weich)
    setZoom(geklemmt)
    setVersatz((alt) => {
      const gerueckt = aufPunkt === null ? alt : zoomeAuf(aufPunkt, alt, zoom, geklemmt)
      return begrenze(geklemmt === 1 ? { x: 0, y: 0 } : gerueckt, geklemmt, bild, fenster())
    })
  }

  /** Der Doppeltipp: eingepasst ⇄ füllend. Bei einem Video passiert nichts. */
  function schalteZoom(punkt: Punkt) {
    if (istVideo) return
    setzeZoom(zoom > 1.01 ? 1 : fuellFaktor(bild, fenster()), punkt, true)
  }

  /**
   * Ein Tipp — aber welcher?
   *
   * **Der erste Tipp wartet auf den zweiten.** Ohne das Warten flackerte die
   * Leiste bei jedem Doppeltipp einmal weg und wieder her. 250 ms sind der
   * Preis dafür, und sie fallen bei einer Blende, die nur auf- und zugeht,
   * nicht auf.
   */
  function tipp(clientX: number, clientY: number) {
    const jetzt = Date.now()
    const vorher = letzterTipp.current
    const nah =
      vorher !== null &&
      jetzt - vorher.zeit < 300 &&
      Math.abs(clientX - vorher.x) < 30 &&
      Math.abs(clientY - vorher.y) < 30

    if (nah) {
      if (tippUhr.current !== null) clearTimeout(tippUhr.current)
      tippUhr.current = null
      letzterTipp.current = null
      schalteZoom(abMitte(clientX, clientY))
      return
    }

    letzterTipp.current = { zeit: jetzt, x: clientX, y: clientY }
    if (tippUhr.current !== null) clearTimeout(tippUhr.current)
    tippUhr.current = setTimeout(() => {
      tippUhr.current = null
      setNackt((war) => !war)
    }, 250)
  }

  const back = () => setIndex((i) => (i - 1 + media.length) % media.length)
  const forward = () => setIndex((i) => (i + 1) % media.length)

  // Ein neues Bild, eine neue Lage -- „Heruntergeladen." unter dem nächsten
  // Foto wäre eine Auskunft über das vorige. Und der Ausschnitt des vorigen
  // Bildes hat mit dem neuen nichts zu tun: jedes fängt eingepasst an.
  useEffect(() => {
    setMeldung(null)
    setNatur(null)
    setSanft(false)
    setZoom(1)
    setVersatz({ x: 0, y: 0 })
  }, [index])

  /**
   * **Das Telefon wird gedreht — und dann fängt das Bild von vorn an.**
   *
   * Gemessen, bevor es diese Zeilen gab, und beides war unbrauchbar:
   *
   * - Am Telefon (390 × 844 → 844 × 390) blieb der 2,9-fache Zoom stehen. Man
   *   dreht aber gerade *deshalb*, weil man das ganze liegende Bild sehen
   *   will; was dastand, war ein Ausschnitt aus einem Foto, das nun beinahe
   *   von selbst passt.
   * - Am iPad (1180 × 820 → 820 × 1180) blieb ein Zoom von 1,24 aus der
   *   Querlage: das Bild ragte seitlich um 97 Punkte hinaus und ließ oben und
   *   unten je 305 Punkte Grau stehen. Ein Zoom, der weder füllt noch passt.
   *
   * **Zurückgesetzt wird nur beim Kippen, nicht bei jeder Änderung.** Auf iOS
   * meldet ein Fenster auch dann `resize`, wenn nur die Werkzeugleisten des
   * Browsers ein- und ausfahren — wer darauf zurücksetzt, nimmt einem Menschen
   * mitten im Betrachten den Ausschnitt weg. Gekippt ist gekippt: hoch wird
   * quer oder umgekehrt.
   *
   * **Der Ausschnitt wird trotzdem bei jeder Änderung nachgezogen.** Ein
   * geteiltes Fenster am iPad wird schmaler, ohne zu kippen — und ein Versatz,
   * der für das breitere Fenster galt, ließe dort Grau am Rand stehen.
   */
  useEffect(() => {
    let quer = window.innerWidth > window.innerHeight
    function gemessen() {
      const jetzt = window.innerWidth > window.innerHeight
      if (jetzt !== quer) {
        quer = jetzt
        setSanft(false)
        setZoom(1)
        setVersatz({ x: 0, y: 0 })
        return
      }
      setVersatz((alt) => begrenze(alt, zoom, bild, fenster()))
    }
    window.addEventListener('resize', gemessen)
    window.addEventListener('orientationchange', gemessen)
    return () => {
      window.removeEventListener('resize', gemessen)
      window.removeEventListener('orientationchange', gemessen)
    }
    // `zoom` und `bild` stehen im Nachziehen -- ohne sie zöge es gegen einen
    // alten Stand nach.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, bild.breite, bild.hoehe])

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'ArrowLeft') back()
      if (event.key === 'ArrowRight') forward()
    }
    window.addEventListener('keydown', onKey)

    // Der Verlauf darunter soll nicht mitscrollen, während oben ein Bild steht.
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = previous
      if (tippUhr.current !== null) clearTimeout(tippUhr.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media.length])

  return createPortal(
    <div
      className={nackt ? 'lightbox lightbox--nackt' : 'lightbox'}
      role="dialog"
      aria-modal="true"
      aria-label={t.chat.grossansicht}
      /*
       * **Ein Klick auf den leeren Bereich schließt nicht mehr.**
       *
       * Hier stand `onClick={onClose}`, und aus dem Betrieb kam: „ein Klick auf
       * einen leeren Bereich: nicht die Galerie schließen, nur über den
       * Schließen-Button." Das trifft auch die Sache: seit die Ansicht
       * randlos ist und ein Doppeltipp sie füllt, gibt es kaum noch einen
       * „leeren Bereich" -- was übrig bleibt, ist ein schmaler Streifen neben
       * dem Foto, und dorthin greift der Daumen beim Wischen und beim
       * Schieben. Ein Bild, das beim Betrachten von selbst zugeht, ist ein
       * kaputter Knopf.
       *
       * **Escape bleibt**, und das ist kein Widerspruch: eine Taste wird
       * absichtlich gedrückt, ein Streifen wird versehentlich getroffen.
       */
      /*
       * **Die Geste endet hier.**
       *
       * Gemeldet aus dem Betrieb: „wenn aus der Highlight-Detailkarte die
       * Fotos geöffnet werden, wird beim Wischen zum nächsten Highlight
       * gewechselt und nicht zum nächsten Foto. Da wird wohl an das falsche
       * Fenster weitergegeben." Genau so war es.
       *
       * **Und der Grund ist eine Eigenheit von `createPortal`:** im DOM hängt
       * dieses Blatt an `document.body`, aber Reacts Ereignisse steigen
       * weiterhin den **React-Baum** hinauf — also dorthin, wo `<Gallery>` im
       * Quelltext steht. Das ist in `HighlightBlatt` mitten im Blatt, und
       * dessen `onTouchEnd` blättert bei einem waagerechten Wisch zum
       * nächsten Punkt. Ein Wisch, zwei Ebenen.
       *
       * **Deshalb steht die Sperre hier und nicht dort.** Die Großansicht
       * liegt obenauf; ihre Gesten sind ihre. So gilt es für jede Ebene
       * darunter, auch für die nächste, die jemand baut — eine Vorsichtsregel
       * in `HighlightBlatt` hätte nur diesen einen Fall gedeckt.
       *
       * **Seit #60 gilt das auch für `onTouchMove`**, und das ist keine
       * Förmlichkeit: das Schieben im vergrößerten Bild ist genau die
       * waagerechte Bewegung, die unten den Punkt weiterblättern würde.
       *
       * **Die Tastatur bleibt davon unberührt** und braucht ihre eigene
       * Sperre (`HighlightBlatt`, `document.querySelector('.lightbox')`): dort
       * hängen beide Zuhörer unmittelbar an `window`, da steigt nichts auf,
       * was man anhalten könnte.
       */
      onTouchStart={(event) => {
        event.stopPropagation()
        /*
         * **Ein Tipp auf einen Knopf ist kein Tipp aufs Bild.**
         *
         * Aus dem Betrieb, mit Aufnahme: „durch den Tipp auf das Emoji wird
         * ganz kurz die Emoji-Auswahl angezeigt, dann ausgeblendet (auch die
         * anderen Knöpfe — wie bei einem einfachen Tipp auf das Foto)". Genau
         * so war es: die Leiste hielt zwar den *Klick* an, aber die
         * Berührungen stiegen weiter bis hierher, und hier steht die Regel
         * „ein Tipp nimmt die Knöpfe weg". Die Auswahl ging auf und
         * verschwand im selben Atemzug mit der Leiste, in der sie hängt.
         *
         * Geprüft wird am Ziel und nicht mit einer zweiten Sperre in der
         * Leiste: so gilt es für jeden Knopf, der später dazukommt.
         */
        /*
         * **Und der Player gehört sich selbst** (#86, 1.56.0). Start,
         * Zeitleiste, Ton und Vollbild sind Tipps und Züge auf derselben
         * Fläche, auf der sonst geblättert und gezoomt wird. Dieselbe
         * Zielprüfung wie für Kopf und Leiste, eine Klasse mehr: so gilt sie
         * auch für das, was Apple und Chrome später in ihre Bedienung
         * einbauen.
         */
        imChrom.current =
          event.target instanceof Element &&
          event.target.closest('.lightbox__bar, .lightbox__kopf, .lightbox__step, .lightbox__video') !== null
        if (imChrom.current) {
          griff.current = null
          hand.current = null
          return
        }
        if (event.touches.length === 2) {
          const [a, b] = [event.touches[0]!, event.touches[1]!]
          hand.current = {
            abstand: abstand(
              { x: a.clientX, y: a.clientY },
              { x: b.clientX, y: b.clientY },
            ),
            zoom,
          }
          griff.current = null
          return
        }
        const finger = event.touches[0]
        if (finger === undefined) return
        griff.current = {
          x: finger.clientX,
          y: finger.clientY,
          versatz,
          zeit: Date.now(),
        }
      }}
      onTouchMove={(event) => {
        event.stopPropagation()
        if (imChrom.current) return

        // Zwei Finger ziehen stufenlos.
        if (hand.current !== null && event.touches.length === 2) {
          const [a, b] = [event.touches[0]!, event.touches[1]!]
          const jetzt = abstand({ x: a.clientX, y: a.clientY }, { x: b.clientX, y: b.clientY })
          if (hand.current.abstand > 0) {
            const mitteX = (a.clientX + b.clientX) / 2
            const mitteY = (a.clientY + b.clientY) / 2
            setzeZoom(
              (hand.current.zoom * jetzt) / hand.current.abstand,
              abMitte(mitteX, mitteY),
              false,
            )
          }
          return
        }

        // Ein Finger schiebt -- aber nur, wenn es etwas zu schieben gibt.
        const zettel = griff.current
        if (zettel === null || zoom <= 1) return
        const finger = event.touches[0]
        if (finger === undefined) return
        setSanft(false)
        setVersatz(
          begrenze(
            {
              x: zettel.versatz.x + (finger.clientX - zettel.x),
              y: zettel.versatz.y + (finger.clientY - zettel.y),
            },
            zoom,
            bild,
            fenster(),
          ),
        )
      }}
      onTouchEnd={(event) => {
        event.stopPropagation()
        zuletztFinger.current = Date.now()
        if (imChrom.current) {
          imChrom.current = false
          return
        }

        if (hand.current !== null) {
          hand.current = null
          // Fast wieder eingepasst: dann ganz. Sonst bliebe ein Ausschnitt bei
          // 1,02 stehen, den niemand gemeint hat.
          if (zoom < 1.05) setzeZoom(1, null, true)
          return
        }

        const zettel = griff.current
        griff.current = null
        if (zettel === null) return

        const finger = event.changedTouches[0]
        const dx = (finger?.clientX ?? zettel.x) - zettel.x
        const dy = (finger?.clientY ?? zettel.y) - zettel.y

        // **Geblättert wird nur eingepasst.** Vergrößert ist dieselbe Bewegung
        // das Schieben des Ausschnitts, und beides zugleich ginge nicht.
        if (zoom <= 1) {
          // Unter 50 Pixeln war es ein Tippen und kein Wischen.
          if (dx > 50) return back()
          if (dx < -50) return forward()
        }

        // Ein Tippen: unter zehn Punkten in beiden Richtungen.
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) {
          tipp(zettel.x, zettel.y)
        }
      }}
    >
      {/* **Eine eigene Zeile und kein Schweben.** Über einem hohen Foto —
          einem Bildschirmfoto etwa — lag der Knopf vorher mitten im Bild:
          weiß auf hell und damit weder zu sehen noch zu treffen.

          Seit #60 liegt er wieder über dem Bild — aber mit einem dunklen
          Verlauf darunter, der genau dieses Problem löst, und er tritt bei
          einem Tipp ganz beiseite. */}
      <div className="lightbox__kopf">
        <button type="button" className="lightbox__close" onClick={onClose} aria-label={t.allgemein.schliessen}>
          <XIcon size={22} />
        </button>
      </div>

      {media.length > 1 && (
        <>
          <button
            type="button"
            className="lightbox__step lightbox__step--back"
            onClick={(event) => {
              event.stopPropagation()
              back()
            }}
            aria-label={t.chat.vorherigesFoto}
          >
            <ChevronLeftIcon size={26} />
          </button>
          <button
            type="button"
            className="lightbox__step lightbox__step--forward"
            onClick={(event) => {
              event.stopPropagation()
              forward()
            }}
            aria-label={t.chat.naechstesFoto}
          >
            <ChevronRightIcon size={26} />
          </button>
        </>
      )}

      {/* **Der Kasten dazwischen ist nicht schmückend.** Ein Bild als
          unmittelbares Kind der Rasterzelle rechnet seinen Deckel
          (`max-height: 100%`) gegen die Zelle -- und WebKit tut das nicht.
          Über einem Bildschirmfoto lag es deshalb über Kreuz und Leiste. Der
          Kasten hat eine gewöhnliche Höhe und schneidet notfalls ab.

          Seit #60 ist er zugleich der Bildschirm: er liegt über der ganzen
          Fläche, und Kopf und Leiste liegen über ihm. */}
      <div
        className="lightbox__flaeche"
        ref={flaeche}
        /* Am Rechner gibt es keinen zweiten Finger — dort zieht das Rad. */
        onWheel={(event) => {
          event.stopPropagation()
          // Ein Video zoomt nicht: sein Bild ist keine Vorlage zum Hineingehen,
          // und das Rad gehört auf dem Rechner seiner Lautstärke.
          if (istVideo) return
          setzeZoom(
            zoom * (1 - event.deltaY / 500),
            abMitte(event.clientX, event.clientY),
            false,
          )
        }}
        onPointerDown={(event) => {
          if (event.pointerType === 'touch' || zoom <= 1) return
          maus.current = { x: event.clientX, y: event.clientY, versatz }
        }}
        onPointerMove={(event) => {
          const zettel = maus.current
          if (zettel === null) return
          setSanft(false)
          setVersatz(
            begrenze(
              {
                x: zettel.versatz.x + (event.clientX - zettel.x),
                y: zettel.versatz.y + (event.clientY - zettel.y),
              },
              zoom,
              bild,
              fenster(),
            ),
          )
        }}
        onPointerUp={() => {
          maus.current = null
        }}
        onPointerLeave={() => {
          maus.current = null
        }}
      >
        {istVideo && media[index] ? (
          /*
           * **Das Video an der Stelle des Bildes** (#86, 1.56.0), in einem
           * eigenen Kasten: an dieser Klasse hält die Zielprüfung weiter
           * oben die Gesten der Großansicht an, damit Start, Zeitleiste und
           * Ton dem Player gehören. Der `key` sorgt dafür, dass beim
           * Blättern ein neues Element entsteht -- so hält das verlassene
           * Video an, statt im Hintergrund weiterzulaufen.
           */
          <div className="lightbox__video" onClick={(event) => event.stopPropagation()}>
            <VideoPlayer key={media[index].id} media={media[index]} />
          </div>
        ) : (
        <img
          className="lightbox__image"
          src={media[index]?.url}
          alt=""
          style={{
            transform: `translate(${versatz.x}px, ${versatz.y}px) scale(${zoom})`,
            transition: sanft ? 'transform 180ms ease-out' : 'none',
            cursor: zoom > 1 ? 'grab' : 'default',
          }}
          onLoad={(event) => {
            const el = event.currentTarget
            if (el.naturalWidth > 0) {
              setNatur({ breite: el.naturalWidth, hoehe: el.naturalHeight })
            }
          }}
          /* Der Klick gehört dem Bild und schließt nicht -- daneben schon. */
          onClick={(event) => {
            event.stopPropagation()
            /*
             * **Nur die Maus, nicht der Finger.**
             *
             * Ein Berührbildschirm schickt nach `touchend` noch einen
             * nachgestellten Klick hinterher — derselbe Tipp käme hier ein
             * zweites Mal an und hätte den Doppeltipp ausgelöst, den niemand
             * gemacht hat. Der Ereignistyp hilft nicht weiter: React nennt
             * das native Ereignis `MouseEvent`, und `detail` steht bei beiden
             * auf eins. Also merkt sich der Finger, wann er zuletzt oben war.
             */
            if (event.detail === 0) return
            if (Date.now() - zuletztFinger.current < 700) return
            tipp(event.clientX, event.clientY)
          }}
        />
        )}
      </div>

      <div className="lightbox__bar" onClick={(event) => event.stopPropagation()}>
        {media[index] && (canReact || reactionsFor(media[index].id).length > 0) && (
          <Reactions
            compact
            reactions={reactionsFor(media[index].id)}
            onPick={(emoji) => canReact && onReact(emoji, media[index]!.id)}
          />
        )}

        <div className="lightbox__aktionen">
          {/* Der Weg dahin, wo das Medium hängt (#86): Beitrag oder POI.
              **The same pill as the buttons beside it** (1.161.0). It was a
              `button--outline` with the page's dark text, and from use:
              "nur bei Fotos, geht das auch bei Videos?" -- it stood at
              videos too, dark on the black around the player, and nobody
              saw it. */}
          {media[index] && wegZu?.(media[index].id) && (
            <Link
              className="lightbox__antwort"
              to={wegZu(media[index].id)!.ziel}
              data-fotos-heimat=""
            >
              {wegZu(media[index].id)!.art === 'punkt' ? <PinIcon size={16} /> : <SprechblaseIcon size={16} />}
              {wegZu(media[index].id)!.wort}
            </Link>
          )}

          {/*
            **Sichern gehört der Anwendung und nicht der Geste** (#58). Auf iOS
            legt ein langes Drücken das Bild in „Fotos", auf Android passiert
            dabei nichts — und wo Chrome dort doch ein Menü zeigt, landet die
            Datei in *Downloads* und nicht in der Galerie. Der Knopf tut auf
            beiden Geräten dasselbe: er reicht das Bild ans System weiter.
          */}
          {/* **And the video too** (1.159.0). Here stood "Sichern gilt dem
              Foto", and from use came: "Video lassen sich aktuell nicht
              herunterladen". A video goes through `saveMedia()`, which on
              iOS asks for a second tap after fetching (reason there). */}
          {media[index] && (
            <button
              type="button"
              className="lightbox__antwort"
              disabled={laeuft}
              onClick={async () => {
                const foto = media[index]
                if (foto === undefined) return
                setLaeuft(true)
                setMeldung(null)
                try {
                  const weg = istVideo
                    ? await saveMedia([foto], saveMediaWords(t, 1))
                    : await sichereFoto(foto)
                  /*
                   * **Nach dem Teilen-Blatt bleibt es still** — das Gerät hat
                   * schon geantwortet, ein zweiter Satz wäre Geplapper.
                   *
                   * **Nach einem Download steht dagegen die eigentliche Frage
                   * im Raum: wo ist es hin?** Auf Android landet es im Ordner
                   * *Downloads*, und den zeigt jede Galerie-App als Album —
                   * das weiß nur, wer es gesagt bekommt. „Heruntergeladen."
                   * stand hier vorher und beantwortete genau das nicht.
                   */
                  if (weg === 'geladen') {
                    setMeldung(istVideo ? t.chat.inDownloads : t.chat.fotoGesichert)
                  }
                } catch {
                  setMeldung(istVideo ? t.chat.videoNichtGeholt : t.chat.fotoNichtGeholt)
                } finally {
                  setLaeuft(false)
                }
              }}
            >
              <SichernIcon size={16} />
              {laeuft ? t.allgemein.laeuft : istVideo ? t.chat.videoSichern : t.chat.fotoSichern}
            </button>
          )}

          {onReply && media[index] && (
            <button
              type="button"
              className="lightbox__antwort"
              onClick={() => {
                onReply(media[index]!.id)
                // Zumachen gehört dazu: geantwortet wird unten im Schreibfeld,
                // und das liegt hinter diesem Bild.
                onClose()
              }}
            >
              <ReplyIcon size={16} />
              {istVideo ? t.chat.aufVideoAntworten : t.chat.aufFotoAntworten}
            </button>
          )}
        </div>

        {meldung !== null && (
          <p className="lightbox__meldung" role="alert">
            {meldung}
          </p>
        )}
        {media.length > 1 && (
          <span className="lightbox__count">{t.chat.fotoZaehler(index + 1, media.length)}</span>
        )}
      </div>
    </div>,
    document.body,
  )
}
